/**
 * Advanced Cost Sheet + Selling Price Engine (v3)
 * - Mirrors "Estimated Project Cost" card from Design tab at the top (single source of truth).
 * - Internal cost breakdown & 3-tier pricing (Vendor → Admin → Client).
 * - Commercial (Overheads / Discount / GST) moved here from Design tab.
 * - Smart Upgrade Suggestions are clickable; applying modifies the parent state in real-time.
 * - Fix: removed double-counting of handles (Hardware now reads only from materialSummary rows).
 */
import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IndianRupee, TrendingUp, AlertTriangle, Lightbulb, Calculator, Plus, Check } from 'lucide-react';

export default function CostSheetEngine({
  calculations = {},
  rates = {},
  config = {},
  // Commercial state (lifted from WardrobeCalculator)
  overhead = { freight: 0, labour_handling: 0, consultancy: 0 },
  setOverhead,
  discount = { type: 'fixed', value: 0 },
  setDiscount,
  gstConfig = { type: 'percent', value: 18 },
  setGstConfig,
  installLabor = { countertop: 0, backsplash: 0 },
  // Smart-upgrade callbacks & state
  onApplySuggestion,
  appliedSuggestions = [],
  estimatedProjectCost = 0,
  designChargePercent = 5,
  isKitchen = false,
  // 3-tier markup state (lifted to parent for PDF/email parity)
  adminMarkup = 20, setAdminMarkup = () => {},
  clientMarkup = 20, setClientMarkup = () => {},
  minMarginAlert = 15, setMinMarginAlert = () => {},
  extraOverheadPct = 0, setExtraOverheadPct = () => {},
}) {
  const [enableGst, setEnableGst] = useState(true);

  const gstPercent = gstConfig.type === 'percent' ? (+gstConfig.value || 0) : 0;
  const gstFixed = gstConfig.type === 'fixed' ? (+gstConfig.value || 0) : 0;

  const costBreakdown = useMemo(() => {
    const c = calculations;
    const HARDWARE_NAME_RE = /(hinge|handle|straightener|sliding channel|drawer slide|tandem|trolley|corner system|lift-?up|stay)/i;
    const matRows = c.materialSummary || [];
    const materialCost = matRows.filter(m => !HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
    const hardwareCost = matRows.filter(m => HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
    const woodworkLabour = c.totalLabor || 0;
    const installLabourCost = (+installLabor.countertop || 0) + (+installLabor.backsplash || 0);
    const laborCost = woodworkLabour + installLabourCost;
    // Internal cost sheet — STRICTLY vendor raw cost, NO overheads here.
    const directCost = materialCost + hardwareCost + laborCost;
    const designCharges = c.designCharges || 0;
    const overheadExtra = directCost * ((+extraOverheadPct || 0) / 100);
    const totalInternalCost = directCost + overheadExtra;

    // 3-tier pricing — clean Vendor → Admin → Client base (before overheads + discount).
    const vendorPrice = totalInternalCost + designCharges;
    const adminPrice = vendorPrice * (1 + adminMarkup / 100);
    const clientBase = adminPrice * (1 + clientMarkup / 100);

    // Commercial overheads applied ON CLIENT side as % of Client Base.
    const transport = clientBase * ((+overhead.freight || 0) / 100);          // Freight / Transportation
    const labourHandling = clientBase * ((+overhead.labour_handling || 0) / 100);
    const consultancy = clientBase * ((+overhead.consultancy || 0) / 100);
    const commercialOverheadTotal = transport + labourHandling + consultancy;
    const clientPreDiscount = clientBase + commercialOverheadTotal;

    // Discount
    const discountAmount = discount.type === 'percent'
      ? clientPreDiscount * ((+discount.value || 0) / 100)
      : (+discount.value || 0);
    const clientPrice = Math.max(0, clientPreDiscount - discountAmount);

    // GST (supports fixed or percent)
    const gstAmount = !enableGst ? 0 : (gstConfig.type === 'percent' ? clientPrice * (gstPercent / 100) : gstFixed);
    const finalClientPrice = clientPrice + gstAmount;

    const grossMargin = clientPrice - vendorPrice;
    const marginPercent = clientPrice > 0 ? (grossMargin / clientPrice) * 100 : 0;
    const lowMargin = marginPercent < minMarginAlert;

    return {
      materialCost, hardwareCost, laborCost, woodworkLabour, installLabourCost,
      transport, labourHandling, consultancy, designCharges, commercialOverheadTotal, clientBase,
      directCost, overheadExtra, totalInternalCost,
      vendorPrice, adminPrice, clientPreDiscount, discountAmount, clientPrice, gstAmount, finalClientPrice,
      grossMargin, marginPercent, lowMargin,
    };
  }, [calculations, extraOverheadPct, enableGst, adminMarkup, clientMarkup, minMarginAlert,
      overhead, installLabor, discount, gstConfig, gstPercent, gstFixed]);

  useEffect(() => {}, [costBreakdown.marginPercent]);

  const fmt = (v) => `Rs ${Math.round(v).toLocaleString('en-IN')}`;

  const suggestions = useMemo(() => {
    const list = [];
    if (!config.includeGlassProfile) list.push({ id: 'glass_profile', label: 'Add Glass Profile Shutter', delta: rates.glassProfileCost || 1200 });
    list.push({ id: 'soft_close', label: 'Upgrade to Soft-Close Hinges', delta: (calculations.totalHinges || 8) * 120 });
    list.push({ id: 'premium_laminate', label: 'Premium External Laminate (Acrylic)', delta: (calculations.externalLaminateSheets || 2) * 500 });
    list.push({ id: 'led_strip', label: 'Add LED Strip Lighting', delta: Math.ceil((config.width || 4)) * 350 });
    return list;
  }, [config, rates, calculations]);

  const Row = ({ label, value, bold, warn, indent }) => (
    <div className={`flex justify-between py-1.5 ${indent ? 'pl-4' : ''} ${bold ? 'font-bold border-t border-stone-300 pt-2' : ''} ${warn ? 'text-red-600' : ''}`}>
      <span className={`text-sm ${bold ? 'text-stone-900' : 'text-stone-600'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'text-stone-900' : ''}`}>{value}</span>
    </div>
  );

  // Estimated Project Cost (single source of truth = Vendor Price from this engine)
  const vendorProjectCost = costBreakdown.vendorPrice;

  return (
    <div className="space-y-5" data-testid="cost-sheet-engine">
      {/* === MIRRORED "Estimated Project Cost" card (real-time) === */}
      <Card className="bg-stone-900 text-white border-0" data-testid="cost-sheet-estimated-project-cost">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-center gap-2 mb-3"><Calculator size={18} className="text-emerald-400" /><span className="text-stone-400 text-xs uppercase font-bold tracking-wide">Estimated Project Cost (Vendor / Raw)</span></div>
          <h2 className="text-3xl sm:text-4xl font-extrabold">{fmt(vendorProjectCost)}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
            <div><span className="text-stone-400">Material</span><p className="font-semibold">{fmt(costBreakdown.materialCost)}</p></div>
            <div><span className="text-stone-400">Hardware</span><p className="font-semibold">{fmt(costBreakdown.hardwareCost)}</p></div>
            <div><span className="text-stone-400">Labour</span><p className="font-semibold">{fmt(costBreakdown.laborCost)}</p></div>
            <div><span className="text-stone-400">Design ({designChargePercent}%)</span><p className="font-semibold">{fmt(costBreakdown.designCharges)}</p></div>
          </div>
          <p className="text-[11px] text-stone-400 mt-3">This value matches the "Estimated Project Cost" on the Design tab and updates in real-time as you change modules, rates, overheads or discounts.</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Internal Cost Sheet */}
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3"><IndianRupee size={18} className="text-emerald-600" /><h3 className="font-bold text-stone-800">Internal Cost Sheet</h3></div>
            <div className="space-y-0.5">
              <Row label="Material Cost" value={fmt(costBreakdown.materialCost)} indent />
              <Row label="Hardware Cost (hinges/handles/trolleys/drawer slides)" value={fmt(costBreakdown.hardwareCost)} indent />
              <Row label={`Labour Cost (woodworking + install)`} value={fmt(costBreakdown.laborCost)} indent />
              <Row label="Direct Cost" value={fmt(costBreakdown.directCost)} bold />
              {(+extraOverheadPct || 0) > 0 && <Row label={`Extra Overhead (${extraOverheadPct}%)`} value={fmt(costBreakdown.overheadExtra)} indent />}
              <Row label="Total Internal Cost" value={fmt(costBreakdown.totalInternalCost)} bold />
              <Row label="Design Charges" value={fmt(costBreakdown.designCharges)} indent />
              <Row label="Subtotal (→ Vendor Price)" value={fmt(costBreakdown.vendorPrice)} bold />
              <Row label={`Gross Margin (${costBreakdown.marginPercent.toFixed(1)}%)`} value={fmt(costBreakdown.grossMargin)} bold warn={costBreakdown.lowMargin} />
              <p className="text-[11px] text-stone-500 mt-2 italic">Freight / Labour Handling / Consultancy overheads are applied on the Client side (see Selling Price Calculator), not here.</p>
            </div>
            {/* Min Margin Alert + Extra Overhead inputs moved here */}
            <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
              <div><Label className="text-xs">Min Margin Alert %</Label><Input type="number" value={minMarginAlert} onChange={e => setMinMarginAlert(+e.target.value)} data-testid="min-margin-alert-input" className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Extra Overhead %</Label><Input type="number" value={extraOverheadPct} onChange={e => setExtraOverheadPct(+e.target.value)} data-testid="extra-overhead-input" className="h-8 text-xs" /></div>
            </div>
            {costBreakdown.lowMargin && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="low-margin-alert">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-red-800 font-semibold">Margin {costBreakdown.marginPercent.toFixed(1)}% is below the {minMarginAlert}% threshold.</p>
                    <p className="text-[11px] text-red-700 mt-0.5">
                      Current Client Markup: {clientMarkup}%. Required to meet threshold: <b>{Math.max(clientMarkup, Math.ceil(((minMarginAlert / (100 - minMarginAlert)) * 100) - ((1 - ((costBreakdown.vendorPrice || 0) / Math.max(costBreakdown.vendorPrice * (1 + adminMarkup / 100), 1))) * 100)))}%</b> or higher.
                    </p>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700"
                      data-testid="auto-adjust-markup-btn"
                      onClick={() => {
                        // Solve: (clientPreGst - vendor) / clientPreGst >= minMargin/100
                        // clientPreGst = vendor / (1 - minMargin/100)
                        // clientMarkup = (clientPreGst / adminPrice - 1) * 100
                        const v = costBreakdown.vendorPrice || 1;
                        const adminPrice = v * (1 + (+adminMarkup || 0) / 100);
                        const targetClientPreGst = v / Math.max(0.01, 1 - ((+minMarginAlert || 0) / 100));
                        const nextClientMarkup = Math.max(0, Math.ceil(((targetClientPreGst / adminPrice) - 1) * 100));
                        setClientMarkup(nextClientMarkup);
                      }}
                    >
                      <TrendingUp size={11} /> Auto-adjust Client Markup to hit {minMarginAlert}%
                    </button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selling Price Calculator (3-Tier Pricing: Vendor → Admin → Client) */}
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3"><TrendingUp size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">Selling Price Calculator</h3><span className="text-xs text-stone-500">(Vendor → Admin → Client)</span></div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><Label>Admin Markup %</Label><Input type="number" value={adminMarkup} onChange={e => setAdminMarkup(+e.target.value)} data-testid="admin-markup-input" /></div>
              <div><Label>Client Markup %</Label><Input type="number" value={clientMarkup} onChange={e => setClientMarkup(+e.target.value)} data-testid="client-markup-input" /></div>
              <div className="flex items-center gap-2 col-span-2">
                <input type="checkbox" checked={enableGst} onChange={e => setEnableGst(e.target.checked)} className="rounded" />
                <span className="text-sm">Apply GST (from Commercial panel)</span>
                {enableGst && <span className="text-xs text-stone-500">{gstConfig.type === 'percent' ? `${gstPercent}%` : `Rs ${gstFixed}`}</span>}
              </div>
            </div>
            <div className="space-y-0.5">
              <Row label="Vendor Price (raw cost + design)" value={fmt(costBreakdown.vendorPrice)} bold />
              <Row label={`+ Admin Markup (${adminMarkup}%)`} value={fmt(costBreakdown.adminPrice - costBreakdown.vendorPrice)} indent />
              <Row label="Admin / Internal Price" value={fmt(costBreakdown.adminPrice)} bold />
              <Row label={`+ Client Markup (${clientMarkup}%)`} value={fmt(costBreakdown.clientBase - costBreakdown.adminPrice)} indent />
              <Row label="Client Base (pre-overheads)" value={fmt(costBreakdown.clientBase)} bold />
              {costBreakdown.transport > 0 && <Row label={`+ Freight / Transportation (${overhead.freight}%)`} value={fmt(costBreakdown.transport)} indent />}
              {costBreakdown.labourHandling > 0 && <Row label={`+ Labour Handling (${overhead.labour_handling}%)`} value={fmt(costBreakdown.labourHandling)} indent />}
              {costBreakdown.consultancy > 0 && <Row label={`+ Consultancy (${overhead.consultancy}%)`} value={fmt(costBreakdown.consultancy)} indent />}
              <Row label="Client Price (pre-discount)" value={fmt(costBreakdown.clientPreDiscount)} bold />
              {costBreakdown.discountAmount > 0 && <Row label={`- Discount (${discount.type === 'percent' ? `${discount.value}%` : `Rs ${discount.value}`})`} value={`- ${fmt(costBreakdown.discountAmount)}`} indent />}
              <Row label="Client Price (pre-GST)" value={fmt(costBreakdown.clientPrice)} bold />
              {enableGst && <Row label={`GST (${gstConfig.type === 'percent' ? `${gstPercent}%` : 'Fixed'})`} value={fmt(costBreakdown.gstAmount)} indent />}
              <Row label="Final Client Price" value={fmt(costBreakdown.finalClientPrice)} bold />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Smart Upgrade Suggestions — clickable */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-3"><Lightbulb size={16} className="text-amber-500" /><h4 className="font-bold text-sm text-stone-800">Smart Upgrade Suggestions</h4><span className="text-xs text-stone-500">(click to apply — updates estimate & costing in real-time)</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {suggestions.map((s, i) => {
              const applied = appliedSuggestions.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => !applied && onApplySuggestion && onApplySuggestion(s.id)}
                  disabled={applied}
                  className={`text-left p-2.5 border rounded-lg transition-colors ${applied ? 'bg-emerald-50 border-emerald-300 cursor-default' : 'bg-amber-50 border-amber-200 hover:bg-amber-100 cursor-pointer'}`}
                  data-testid={`suggestion-${s.id}`}
                >
                  <p className="text-xs font-medium text-stone-800 flex items-center gap-1">
                    {applied ? <Check size={12} className="text-emerald-600" /> : <Plus size={12} className="text-amber-600" />}{s.label}
                  </p>
                  <p className={`text-sm font-bold mt-0.5 ${applied ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {applied ? 'Applied' : `+${fmt(s.delta)}`}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Commercial — Overheads, Discount & GST (moved from Design tab) */}
      {setOverhead && (
        <Card data-testid="commercial-overheads-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3"><IndianRupee size={18} className="text-emerald-600" /><h3 className="font-bold text-stone-800">Commercial — Overheads, Discount & GST</h3><span className="text-xs text-stone-500">(real-time — reflects in all costs above)</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Freight (%)</Label><Input type="number" step="0.1" value={overhead.freight} onChange={e => setOverhead({ ...overhead, freight: +e.target.value })} data-testid="overhead-freight" /></div>
              <div><Label>Labour Handling (%)</Label><Input type="number" step="0.1" value={overhead.labour_handling} onChange={e => setOverhead({ ...overhead, labour_handling: +e.target.value })} data-testid="overhead-labour" /></div>
              <div><Label>Consultancy (%)</Label><Input type="number" step="0.1" value={overhead.consultancy} onChange={e => setOverhead({ ...overhead, consultancy: +e.target.value })} data-testid="overhead-consultancy" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <div>
                <Label>Discount Type</Label>
                <select value={discount.type} onChange={e => setDiscount({ ...discount, type: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm">
                  <option value="fixed">Fixed Amount (Rs)</option>
                  <option value="percent">Percentage (%)</option>
                </select>
              </div>
              <div><Label>Discount Value</Label><Input type="number" value={discount.value} onChange={e => setDiscount({ ...discount, value: +e.target.value })} data-testid="discount-value" /></div>
              <div>
                <Label>GST Type</Label>
                <select value={gstConfig.type} onChange={e => setGstConfig({ ...gstConfig, type: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm">
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (Rs)</option>
                </select>
              </div>
              <div><Label>GST Value</Label><Input type="number" value={gstConfig.value} onChange={e => setGstConfig({ ...gstConfig, value: +e.target.value })} data-testid="gst-value" /></div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
